import React, { useMemo, useState } from 'react'
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import VisibilityIcon from '@mui/icons-material/Visibility'
import SendIcon from '@mui/icons-material/Send'
import PersonIcon from '@mui/icons-material/Person'
import FlagIcon from '@mui/icons-material/Flag'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import useIsMobile from '../../../../helpers/useIsMobile'

export const FILTERS = [
  { key: 'all', label: 'Everyone', match: () => true },
  {
    key: 'outstanding',
    label: 'Outstanding',
    match: (person) => person.state !== 'complete',
  },
  {
    key: 'flagged',
    label: 'Flagged',
    match: (person) => (person.latestSubmission?.flags?.length ?? 0) > 0,
  },
  {
    key: 'appraisal',
    label: 'Awaiting appraisal refresh',
    match: (person) => person.appraisalRefreshNeeded,
  },
  {
    key: 'complete',
    label: 'Screened',
    match: (person) => person.state === 'complete',
  },
]

const STATE_CHIP = {
  complete: { label: 'Screened', color: 'success' },
  requested: { label: 'Requested', color: 'warning' },
  never: { label: 'Never completed', color: 'error' },
}

const fullName = (person) =>
  `${person.userAccount?.firstName ?? ''} ${
    person.userAccount?.lastName ?? ''
  }`.trim() || 'Unknown'

// Ranks the worklist so the people needing action sit at the top before
// any column sort is applied
const priority = (person) => {
  if (person.state === 'never') return 0
  if (person.state === 'requested') return 1
  if ((person.latestSubmission?.flags?.length ?? 0) > 0) return 2
  if (person.appraisalRefreshNeeded) return 3
  return 4
}

// Every active employee with their screening state, latest flags and the
// actions a reviewer can take on them
const OHStaffTable = ({
  staff = [],
  filter,
  onFilterChange,
  onView,
  onRequest,
  requestingId,
}) => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [search, setSearch] = useState('')

  const counts = useMemo(
    () =>
      Object.fromEntries(
        FILTERS.map((item) => [item.key, staff.filter(item.match).length]),
      ),
    [staff],
  )

  const rows = useMemo(() => {
    const active = FILTERS.find((item) => item.key === filter) ?? FILTERS[0]
    const needle = search.trim().toLowerCase()
    return (
      staff
        .filter(active.match)
        .filter(
          (person) =>
            !needle ||
            fullName(person).toLowerCase().includes(needle) ||
            (person.clinicalRole ?? '').toLowerCase().includes(needle),
        )
        .map((person) => ({ ...person, priority: priority(person) }))
        // Name order within each priority band - the grid only sorts on one
        // column in the community edition, so ties are settled here
        .sort((a, b) => fullName(a).localeCompare(fullName(b)))
    )
  }, [staff, filter, search])

  const openProfile = (person) => {
    if (person.userAccount?._id) {
      navigate(`/employees/${person.userAccount._id}?tab=occupationalhealth`)
    }
  }

  const columns = [
    {
      field: 'name',
      headerName: 'Employee',
      flex: 1,
      minWidth: 200,
      valueGetter: (value, row) => fullName(row),
      renderCell: ({ row }) => (
        <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
          <Avatar
            src={row.userAccount?.photograph}
            sx={{ width: 32, height: 32, borderRadius: 2 }}
          />
          <Box minWidth={0}>
            <Typography fontWeight={600} noWrap sx={{ fontSize: '0.9rem' }}>
              {fullName(row)}
            </Typography>
            {row.clinicalRole && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.clinicalRole}
              </Typography>
            )}
          </Box>
        </Stack>
      ),
    },
    {
      field: 'clinicalRole',
      headerName: 'Role',
      minWidth: 150,
      flex: 0.6,
    },
    {
      field: 'state',
      headerName: 'Status',
      headerAlign: 'center',
      minWidth: 160,
      valueGetter: (value, row) => STATE_CHIP[row.state]?.label ?? row.state,
      renderCell: ({ row }) => {
        const chip = STATE_CHIP[row.state] ?? STATE_CHIP.never
        return (
          <Stack
            // direction="row"
            gap={0.5}
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Chip
              size="small"
              label={chip.label}
              color={chip.color}
              variant={row.state === 'complete' ? 'outlined' : 'filled'}
            />
            {row.appraisalRefreshNeeded && (
              <Tooltip title="An open appraisal is waiting for this person to confirm or update their screening">
                <Chip size="small" label="Appraisal" variant="outlined" />
              </Tooltip>
            )}
          </Stack>
        )
      },
    },
    {
      field: 'lastSubmittedAt',
      headerName: 'Last screening',
      headerAlign: 'center',
      minWidth: 170,
      valueGetter: (value, row) =>
        row.lastSubmittedAt
          ? moment(row.lastSubmittedAt).format('YYYY-MM-DD')
          : '',
      renderCell: ({ row }) => (
        <Stack alignItems="center" justifyContent="center" height="100%">
          {row.lastSubmittedAt ? (
            <Box>
              <Typography sx={{ fontSize: '0.85rem' }}>
                {moment(row.lastSubmittedAt).format('D MMM YYYY')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {moment(row.lastSubmittedAt).fromNow()}
              </Typography>
            </Box>
          ) : row.requestedAt ? (
            <Typography variant="caption" color="text.secondary">
              Requested {moment(row.requestedAt).fromNow()}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      field: 'flags',
      headerName: 'Flags',
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => row.latestSubmission?.flags?.length ?? 0,
      renderCell: ({ row }) => {
        const flags = row.latestSubmission?.flags ?? []
        if (!row.latestSubmission) return null
        if (flags.length === 0) {
          return (
            <Chip
              size="small"
              label="Clear"
              color="success"
              variant="outlined"
            />
          )
        }
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Tooltip
              title={
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {flags.map((flag) => (
                    <li key={flag}>{flag}</li>
                  ))}
                </Box>
              }
            >
              <Chip
                size="small"
                color="warning"
                icon={<FlagIcon />}
                label={flags.length}
              />
            </Tooltip>
          </Box>
        )
      },
    },
    {
      field: 'bmi',
      headerName: 'BMI',
      minWidth: 90,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => row.latestSubmission?.bmi ?? null,
      renderCell: ({ row }) =>
        row.latestSubmission?.bmi != null ? (
          <Box
            display="flex"
            height="100%"
            justifyContent="center"
            alignItems="center"
          >
            <Typography
              sx={{
                fontSize: '0.85rem',
                fontVariantNumeric: 'tabular-nums',
                fontWeight: row.latestSubmission.bmi >= 40 ? 600 : 400,
              }}
            >
              {row.latestSubmission.bmi}
            </Typography>
          </Box>
        ) : null,
    },
    {
      field: 'evidence',
      headerName: 'Evidence',
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => row.latestSubmission?.evidenceCount ?? 0,
      renderCell: ({ row }) =>
        (row.latestSubmission?.evidenceCount ?? 0) > 0 ? (
          <Tooltip
            title={`${row.latestSubmission.evidenceCount} immunisation evidence file${
              row.latestSubmission.evidenceCount === 1 ? '' : 's'
            }`}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={0.25}
              justifyContent="center"
              height="100%"
            >
              <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption">
                {row.latestSubmission.evidenceCount}
              </Typography>
            </Stack>
          </Tooltip>
        ) : null,
    },
    {
      field: 'priority',
      headerName: 'Priority',
      minWidth: 90,
      type: 'number',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 150,
      sortable: false,
      filterable: false,
      disableExport: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Stack
          direction="row"
          gap={0.25}
          onClick={(e) => e.stopPropagation()}
          height="100%"
          alignItems="center"
        >
          <Tooltip title="View latest screening">
            <span>
              <IconButton
                size="small"
                disabled={!row.latestSubmission}
                onClick={() => onView(row.latestSubmission._id)}
                aria-label="View latest screening"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              row.state === 'requested'
                ? 'Screening already requested'
                : 'Request a new screening'
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={row.state === 'requested' || requestingId === row._id}
                onClick={() => onRequest(row)}
                aria-label="Request a new screening"
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Open employee profile">
            <span>
              <IconButton
                size="small"
                disabled={!row.userAccount?._id}
                onClick={() => openProfile(row)}
                aria-label="Open employee profile"
              >
                <PersonIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ]

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', lg: 'center' }}
        gap={1.5}
      >
        <Stack>
          <Typography variant="title">Staff screening worklist</Typography>
          <Typography variant="smallGrey">
            Every active employee — people needing action first
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderRadius: 3,
            padding: '2px 12px',
            border: '1px solid',
            borderColor: 'divider',
            width: { xs: '100%', lg: 320 },
          }}
        >
          <SearchIcon sx={{ opacity: 0.6 }} />
          <TextField
            variant="standard"
            placeholder="Search by name or role…"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ disableUnderline: true }}
          />
          {search && (
            <IconButton
              size="small"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Stack>

      <Box sx={{ overflowX: 'auto', mt: 1.5 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={filter}
          onChange={(e, value) => value && onFilterChange(value)}
          aria-label="Filter staff"
          sx={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}
        >
          {FILTERS.map((item) => (
            <ToggleButton
              key={item.key}
              value={item.key}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', gap: 0.75 }}
            >
              {item.label}
              <Chip
                size="small"
                label={counts[item.key] ?? 0}
                sx={{ height: 18, fontSize: '0.7rem' }}
              />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box mt={1.5}>
        <CustomDataGrid
          autoHeight
          rows={rows}
          columns={columns}
          getRowId={(row) => row._id}
          // Employee and Last screening cells stack two lines - the default
          // 52px row clips them
          rowHeight={64}
          onRowClick={({ row }) => openProfile(row)}
          disableColumnFilter
          disableColumnSelector
          documentTitle="Occupational Health Screening"
          initialState={{
            sorting: {
              sortModel: [{ field: 'priority', sort: 'asc' }],
            },
            columns: {
              columnVisibilityModel: {
                clinicalRole: false,
                priority: false,
              },
            },
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
            // The grid sets every cell's line-height to the row height, and
            // the theme's body/caption text inherits it - so a two-line cell
            // becomes two row-heights tall and overflows. Reset it here.
            '& .MuiDataGrid-cell': { lineHeight: 1.4 },
          }}
        />
      </Box>
    </Paper>
  )
}

export default OHStaffTable
